<template>
  <!-- Portal dos pais: layout isolado -->
  <router-view v-if="isPortalRoute" />

  <!-- Página de login: sem sidebar/topbar -->
  <router-view v-else-if="!auth.isAuthenticated" />

  <div v-else class="app">

    <!-- ─────────────────────────────────────────
         SIDEBAR
    ───────────────────────────────────────── -->
    <aside class="sidebar" :class="{ 'is-collapsed': collapsed }">

      <!-- Logo Octagon 20 Anos -->
      <div class="sidebar-logo">
        <div class="logo-mark">
          <img src="/octagon-logo-20anos.png" alt="Octagon 20 Anos" :class="collapsed ? 'logo-image-collapsed' : 'logo-image-full'" />
        </div>
      </div>

      <!-- Nav groups -->
      <nav class="sidebar-nav">

        <div class="nav-group" v-for="group in navGroups" :key="group.key">
          <transition name="label-fade">
            <p class="nav-group-label" v-if="!collapsed">{{ group.label }}</p>
          </transition>

          <router-link
            v-for="item in group.items"
            :key="item.name"
            :to="{ name: item.name }"
            class="nav-link"
            :class="{ 'is-active': $route.name === item.name }"
            :title="collapsed ? item.meta.title : undefined"
          >
            <span class="nav-icon" v-html="svgIcon(item.meta.iconPath)"></span>
            <transition name="label-fade">
              <span class="nav-label" v-if="!collapsed">{{ item.meta.title }}</span>
            </transition>
            <span class="nav-dot" v-if="$route.name === item.name && !collapsed"></span>
          </router-link>
        </div>

      </nav>

      <!-- Footer -->
      <div class="sidebar-bottom">
        <!-- Status -->
        <div class="sidebar-status" :class="isOnline ? 'status-on' : 'status-off'" :title="isOnline ? 'Conectado' : 'Sem conexão'">
          <span class="status-led"></span>
          <transition name="label-fade">
            <span class="status-text" v-if="!collapsed">{{ isOnline ? 'Online' : 'Offline' }}</span>
          </transition>
          <transition name="label-fade">
            <span class="status-badge" v-if="!collapsed && pendingSync > 0">{{ pendingSync }}</span>
          </transition>
        </div>

        <!-- Collapse toggle -->
        <button class="collapse-btn" @click="collapsed = !collapsed" :title="collapsed ? 'Expandir' : 'Recolher'">
          <svg viewBox="0 0 20 20" fill="currentColor" class="collapse-icon" :class="{ 'is-flipped': collapsed }">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          <transition name="label-fade">
            <span v-if="!collapsed">Recolher</span>
          </transition>
        </button>
      </div>

    </aside>

    <!-- ─────────────────────────────────────────
         MAIN AREA
    ───────────────────────────────────────── -->
    <div class="main-area">

      <!-- TOPBAR -->
      <header class="topbar">
        <div class="topbar-left">
          <!-- Breadcrumb -->
          <div class="breadcrumb">
            <span class="breadcrumb-root">Colégio Octagon</span>
            <svg class="breadcrumb-sep" viewBox="0 0 16 16" fill="currentColor">
              <path fill-rule="evenodd" d="M6.22 3.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z" clip-rule="evenodd"/>
            </svg>
            <span class="breadcrumb-current">{{ currentPageTitle }}</span>
          </div>
        </div>

        <div class="topbar-right">
          <!-- Sync button -->
          <button
            v-if="pendingSync > 0 && isOnline"
            class="topbar-btn sync-btn"
            @click="syncNow"
            :disabled="isSyncing"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" :class="{ 'spin': isSyncing }">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
            </svg>
            <span>Sincronizar</span>
            <span class="sync-count">{{ pendingSync }}</span>
          </button>

          <!-- Divider -->
          <div class="topbar-divider"></div>

          <!-- User + Logout -->
          <div class="topbar-user" @click="menuUsuarioAberto = !menuUsuarioAberto" :class="{ 'is-open': menuUsuarioAberto }" ref="userMenuRef">
            <div class="user-avatar">{{ auth.iniciais }}</div>
            <div class="user-info">
              <span class="user-name">{{ auth.nomeUsuario }}</span>
              <span class="user-role">{{ auth.perfil === 'admin' ? 'Administrador' : 'Operador' }}</span>
            </div>
            <svg class="user-chevron" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </div>

          <!-- Dropdown do usuário -->
          <transition name="dropdown">
            <div v-if="menuUsuarioAberto" class="user-dropdown">
              <div class="dropdown-header">
                <div class="user-avatar sm">{{ auth.iniciais }}</div>
                <div>
                  <div class="dropdown-name">{{ auth.nomeUsuario }}</div>
                  <div class="dropdown-role">{{ auth.usuario?.email || auth.usuario?.usuario }}</div>
                </div>
              </div>
              <div class="dropdown-divider"></div>
              <button class="dropdown-item danger" @click="handleLogout">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clip-rule="evenodd"/>
                </svg>
                Sair
              </button>
            </div>
          </transition>
        </div>
      </header>

      <!-- PAGE CONTENT -->
      <main class="page-content">
        <router-view v-slot="{ Component }">
          <transition name="page" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </main>

    </div>

    <!-- ─────────────────────────────────────────
         TOASTS
    ───────────────────────────────────────── -->
    <div class="toasts">
      <transition-group name="toast">
        <div v-for="t in toasts" :key="t.id" class="toast" :class="`toast-${t.type}`">
          <div class="toast-icon-wrap">
            <svg v-if="t.type==='success'" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <svg v-else-if="t.type==='error'" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
            <svg v-else-if="t.type==='warning'" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
            <svg v-else viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
          </div>
          <span class="toast-msg">{{ t.message }}</span>
          <button class="toast-close" @click="removeToast(t.id)">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
          </button>
        </div>
      </transition-group>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useVendaStore } from './stores/vendaStore'
import { useAuthStore } from './stores/authStore'
import syncService from './services/SyncService'
import offlineStorage from './services/OfflineStorage'

const route  = useRoute()
const router = useRouter()
const store  = useVendaStore()
const auth   = useAuthStore()

const isPortalRoute = computed(() => route.path === '/portal' || route.path.startsWith('/portal/'))

const collapsed        = ref(false)
const isOnline         = ref(navigator.onLine)
const isSyncing        = ref(false)
const pendingSync      = ref(0)
const toasts           = ref([])
const menuUsuarioAberto = ref(false)
const userMenuRef       = ref(null)

async function handleLogout() {
  menuUsuarioAberto.value = false
  await auth.logout()
  router.replace({ name: 'Login' })
}

// Fechar menu ao clicar fora
function handleClickOutside(e) {
  if (userMenuRef.value && !userMenuRef.value.contains(e.target)) {
    menuUsuarioAberto.value = false
  }
}

// ── helpers ──────────────────────────────────────────────────
const svgIcon = (path) =>
  `<svg viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">${path}</svg>`

// ── nav groups ────────────────────────────────────────────────
const GROUP_META = {
  principal:  'Principal',
  cadastros:  'Cadastros',
  financeiro: 'Financeiro',
  sistema:    'Sistema',
}

const navGroups = computed(() => {
  const all = router.getRoutes().filter(r => r.meta?.group)
  const map = {}
  all.forEach(r => {
    const g = r.meta.group
    if (!map[g]) map[g] = []
    map[g].push(r)
  })
  return Object.entries(map).map(([key, items]) => ({
    key,
    label: GROUP_META[key] ?? key,
    items,
  }))
})

const currentPageTitle = computed(() => {
  const r = router.getRoutes().find(r => r.name === route.name)
  return r?.meta?.title ?? ''
})

// ── sync / online ─────────────────────────────────────────────
const updateStatus = () => { isOnline.value = navigator.onLine; store.updateOnlineStatus() }
const checkPending = async () => { pendingSync.value = await offlineStorage.contarVendasPendentes() }

const syncNow = async () => {
  if (isSyncing.value) return
  isSyncing.value = true
  const res = await syncService.syncPendingData()
  isSyncing.value = false
  showToast(res.success ? res.message : (res.message || 'Erro ao sincronizar'), res.success ? 'success' : 'error')
  await checkPending()
}

// ── toasts ────────────────────────────────────────────────────
let tid = 0
const showToast = (message, type = 'info', ms = 4000) => {
  const id = ++tid
  toasts.value.push({ id, message, type })
  setTimeout(() => removeToast(id), ms)
}
const removeToast = (id) => {
  const i = toasts.value.findIndex(t => t.id === id)
  if (i > -1) toasts.value.splice(i, 1)
}

provide('showToast', showToast)
provide('checkPendingSync', checkPending)

// ── lifecycle ─────────────────────────────────────────────────
let syncUnsub = null
onMounted(() => {
  window.addEventListener('online',  updateStatus)
  window.addEventListener('offline', updateStatus)
  document.addEventListener('click', handleClickOutside)
  store.updatePendingSyncStatus()
  checkPending()
  const iv = setInterval(checkPending, 30000)
  syncUnsub = syncService.onStatusChange(s => { if (s === 'synced' || s === 'online') checkPending() })
  store.setupListeners()
  onUnmounted(() => {
    window.removeEventListener('online',  updateStatus)
    window.removeEventListener('offline', updateStatus)
    document.removeEventListener('click', handleClickOutside)
    clearInterval(iv)
    if (syncUnsub) syncUnsub()
  })
})
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   TOKENS
═══════════════════════════════════════════════════════════ */
.app {
  --sb-w:      248px;
  --sb-w-min:  68px;
  --tb-h:      60px;
  --sb-bg:     #ffffff;
  --sb-border: #EEF0F2;
  --sb-text:   #6A6C72;
  --sb-hover:  rgba(1,3,158,0.04);
  --sb-active: rgba(1,3,158,0.08);
  --sb-accent: #01039E;
  --tb-bg:     #ffffff;
  --page-bg:   #F9F9F9;
  --radius:    10px;
  --font:      'Barlow', -apple-system, BlinkMacSystemFont, sans-serif;

  display: flex;
  min-height: 100vh;
  font-family: var(--font);
  -webkit-font-smoothing: antialiased;
  background: var(--page-bg);
}

/* ═══════════════════════════════════════════════════════════
   SIDEBAR
═══════════════════════════════════════════════════════════ */
.sidebar {
  width: var(--sb-w);
  min-height: 100vh;
  background: var(--sb-bg);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid var(--sb-border);
  z-index: 100;
}

.sidebar.is-collapsed {
  width: var(--sb-w-min);
}

/* ── Logo ── */
.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border-bottom: 1px solid var(--sb-border);
  flex-shrink: 0;
  overflow: hidden;
  min-height: 68px;
}

.logo-mark {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-mark svg {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: block;
  border-radius: 8px;
}

.logo-image-full {
  height: 125px;
  width: auto;
  object-fit: contain;
  display: block;
  max-width: 100%;
  transition: height 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo-image-collapsed {
  height: 40px;
  width: auto;
  object-fit: contain;
  display: block;
  max-width: 100%;
  transition: height 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ── Nav ── */
.sidebar-nav {
  flex: 1;
  padding: 12px 8px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
  scrollbar-width: none;
}
.sidebar-nav::-webkit-scrollbar { display: none; }

.nav-group {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 4px;
}

.nav-group + .nav-group {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--sb-border);
}

.nav-group-label {
  font-size: 0.62rem;
  font-weight: 700;
  color: #9aa0a8;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  padding: 0 12px 8px;
  white-space: nowrap;
  overflow: hidden;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius);
  text-decoration: none;
  color: var(--sb-text);
  font-size: 0.86rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  transition: background 0.15s, color 0.15s;
  position: relative;
}

.sidebar.is-collapsed .nav-link {
  justify-content: center;
  padding: 10px;
}

.nav-link:hover {
  background: var(--sb-hover);
  color: #082434;
}

.nav-link.is-active {
  background: var(--sb-active);
  color: var(--sb-accent);
  font-weight: 600;
}

.nav-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.nav-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* indicador ativo — bullet à esquerda */
.nav-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--sb-accent);
  flex-shrink: 0;
  margin-left: auto;
}

/* ── Bottom ── */
.sidebar-bottom {
  padding: 10px 8px 14px;
  border-top: 1px solid var(--sb-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.sidebar-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: var(--radius);
  font-size: 0.75rem;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
}

.sidebar.is-collapsed .sidebar-status {
  justify-content: center;
  padding: 8px;
}

.status-on  { color: #4ade80; background: rgba(74,222,128,0.10); }
.status-off { color: #fbbf24; background: rgba(251,191,36,0.10); }

.status-led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.status-on .status-led {
  animation: led-pulse 2.2s ease-in-out infinite;
}

@keyframes led-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
}

.status-text { flex: 1; }

.status-badge {
  background: #E61439;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 99px;
  min-width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.collapse-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: var(--radius);
  border: none;
  background: transparent;
  color: #9aa0a8;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  font-family: var(--font);
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  text-align: left;
}

.sidebar.is-collapsed .collapse-btn {
  justify-content: center;
  padding: 9px;
}

.collapse-btn:hover {
  background: var(--sb-hover);
  color: #082434;
}

.collapse-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform 0.22s cubic-bezier(0.4,0,0.2,1);
}

.collapse-icon.is-flipped {
  transform: rotate(180deg);
}

/* ═══════════════════════════════════════════════════════════
   MAIN AREA
═══════════════════════════════════════════════════════════ */
.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

/* ── Topbar ── */
.topbar {
  height: var(--tb-h);
  background: var(--tb-bg);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 50;
  gap: 16px;
  overflow: visible;
}

.topbar-left { display: flex; align-items: center; gap: 12px; }
.topbar-right { display: flex; align-items: center; gap: 12px; }

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
}

.breadcrumb-root {
  color: #6A6C72;
  font-weight: 500;
}

.breadcrumb-sep {
  width: 14px;
  height: 14px;
  color: #c8cbd1;
  flex-shrink: 0;
}

.breadcrumb-current {
  color: #082434;
  font-weight: 600;
}

/* Sync button */
.topbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  background: #F9F9F9;
  color: #374151;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--font);
  transition: all 0.15s;
}

.topbar-btn:hover:not(:disabled) {
  background: #f1f3f5;
  border-color: #c8cbd1;
}

.topbar-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.topbar-btn svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.sync-btn svg.spin {
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.sync-count {
  background: #2480C1;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 99px;
  min-width: 16px;
  text-align: center;
}

/* Divider */
.topbar-divider {
  width: 1px;
  height: 24px;
  background: #e5e7eb;
  flex-shrink: 0;
}

/* User */
.topbar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: var(--radius);
  transition: background 0.15s;
}

.topbar-user:hover { background: #F9F9F9; }

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #01039E, #082434);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0;
}

.user-avatar.sm {
  width: 28px;
  height: 28px;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.user-chevron {
  width: 14px;
  height: 14px;
  color: #6A6C72;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.topbar-user.is-open .user-chevron {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: calc(var(--tb-h) + 4px);
  right: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(8,36,52,0.14), 0 2px 8px rgba(8,36,52,0.06);
  min-width: 220px;
  z-index: 200;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
}

.dropdown-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #082434;
}

.dropdown-role {
  font-size: 0.72rem;
  color: #6A6C72;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.dropdown-divider {
  height: 1px;
  background: #f1f3f5;
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
  text-align: left;
}

.dropdown-item:hover { background: #F9F9F9; }

.dropdown-item.danger { color: #E61439; }
.dropdown-item.danger:hover { background: #fdeaee; }

.dropdown-item svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.dropdown-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.dropdown-leave-active { transition: opacity 0.1s ease; }
.dropdown-enter-from   { opacity: 0; transform: translateY(-8px) scale(0.97); }
.dropdown-leave-to     { opacity: 0; }

.user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.user-name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #082434;
}

.user-role {
  font-size: 0.65rem;
  color: #6A6C72;
  font-weight: 500;
}

/* ── Page content ── */
.page-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 28px;
}

/* ═══════════════════════════════════════════════════════════
   TOASTS
═══════════════════════════════════════════════════════════ */
.toasts {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 360px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(8,36,52,0.14), 0 1px 4px rgba(8,36,52,0.06);
  border: 1px solid #e5e7eb;
  pointer-events: all;
}

.toast-icon-wrap {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-icon-wrap svg { width: 18px; height: 18px; }

.toast-success .toast-icon-wrap { color: #16a34a; }
.toast-error   .toast-icon-wrap { color: #E61439; }
.toast-warning .toast-icon-wrap { color: #d97706; }
.toast-info    .toast-icon-wrap { color: #2480C1; }

.toast-msg {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
}

.toast-close {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0;
  transition: all 0.15s;
}

.toast-close:hover { background: #f3f4f6; color: #374151; }
.toast-close svg { width: 14px; height: 14px; }

/* ═══════════════════════════════════════════════════════════
   TRANSITIONS
═══════════════════════════════════════════════════════════ */
.label-fade-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.label-fade-leave-active { transition: opacity 0.1s ease; position: absolute; }
.label-fade-enter-from   { opacity: 0; transform: translateX(-4px); }
.label-fade-leave-to     { opacity: 0; }

.page-enter-active { transition: opacity 0.16s ease, transform 0.16s ease; }
.page-leave-active { transition: opacity 0.12s ease; }
.page-enter-from   { opacity: 0; transform: translateY(8px); }
.page-leave-to     { opacity: 0; }

.toast-enter-active { transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1); }
.toast-leave-active { transition: all 0.18s ease; }
.toast-enter-from   { opacity: 0; transform: translateY(12px) scale(0.95); }
.toast-leave-to     { opacity: 0; transform: translateX(20px); }

/* ═══════════════════════════════════════════════════════════
   RESPONSIVE
═══════════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .app { flex-direction: column; }

  .sidebar {
    width: 100% !important;
    height: auto;
    min-height: auto;
    position: sticky;
    top: 0;
    flex-direction: row;
    border-right: none;
    border-bottom: 1px solid var(--sb-border);
  }

  .sidebar-logo { border-bottom: none; border-right: 1px solid var(--sb-border); padding: 12px 14px; min-height: auto; }
  .logo-text, .logo-sub { display: none; }

  .sidebar-nav {
    flex-direction: row;
    padding: 8px;
    gap: 2px;
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .nav-group { flex-direction: row; margin: 0; padding: 0; border: none !important; gap: 2px; }
  .nav-group-label { display: none; }
  .nav-link { padding: 8px 10px; border-radius: 6px; }
  .nav-label, .nav-dot { display: none; }

  .sidebar-bottom { display: none; }

  .topbar { padding: 0 16px; }
  .breadcrumb-root { display: none; }
  .breadcrumb-sep  { display: none; }
  .user-info       { display: none; }
  .topbar-divider  { display: none; }

  .page-content { padding: 16px; }

  .toasts { bottom: 16px; right: 16px; left: 16px; max-width: none; }
}
</style>
